import React, { useState } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  Flex,
  useColorModeValue,
  Button,
  Icon,
} from '@chakra-ui/react';
import { HelpCircle, MessageSquare } from 'lucide-react';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

interface QASlideProps {
  onOpenJIMIChat?: () => void;
  onShowUISample?: (sampleType: string) => void;
}

export default function QASlide({ onOpenJIMIChat, onShowUISample }: QASlideProps) {
  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const accentColor = useColorModeValue('teal.600', 'teal.300');
  
  return (
    <Box
      height="100vh"
      width="100%"
      bg={bgColor}
      position="relative"
      overflow="hidden"
    >
      <Flex 
        direction="column" 
        height="100%" 
        justify="center" 
        align="center"
        p={8}
      >
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          maxWidth="1000px"
          width="100%"
        >
          <VStack spacing={8} align="center" textAlign="center">
            <Heading as="h1" size="2xl" color={accentColor}>
              Questions & Answers
            </Heading>
            
            <Text fontSize="xl" color={textColor} maxW="800px">
              Thank you for viewing our JIMI Seed FUNds proposal. We welcome your questions and feedback.
            </Text>
            
            <Box 
              p={8} 
              bg="gray.50" 
              borderRadius="lg" 
              boxShadow="md"
              width="100%"
              maxW="800px"
              textAlign="left"
            >
              <VStack spacing={6} align="flex-start">
                <Heading as="h3" size="md" color={accentColor}>
                  Common Questions
                </Heading>
                
                <VStack spacing={4} align="flex-start" width="100%">
                  <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg="white">
                    <Text fontWeight="bold">What makes JIMI different from other wellness platforms?</Text>
                    <Text mt={2}>
                      JIMI is unique in addressing all three dimensions of the Professional Fulfillment Model simultaneously: Resilience, Culture of Wellness, and Practice Efficiency. Our AI-powered approach provides personalized support while collecting valuable data to drive continuous improvement.
                    </Text>
                  </Box>
                  
                  <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg="white">
                    <Text fontWeight="bold">How will JIMI integrate with existing systems?</Text>
                    <Text mt={2}>
                      JIMI is designed with integration capabilities in mind. Potential integrations will be explored in Phase 2, such as Epic's Signal Report Data integration, while maintaining strict data privacy and security standards. The platform delivers value independently while these integration opportunities are evaluated.
                    </Text>
                  </Box>
                  
                  <Box p={4} borderLeft="4px solid" borderColor={accentColor} bg="white">
                    <Text fontWeight="bold">What are the expected benefits for Houston Methodist?</Text>
                    <Text mt={2}>
                      Based on our analysis, the JIMI Wellness Platform will deliver significant improvements in clinician wellness through reduced burnout, enhanced community support, and optimized practice efficiency. These improvements will positively impact clinician retention, job satisfaction, and overall quality of care.
                    </Text>
                  </Box>
                </VStack>
                
                <Flex width="100%" justify="center" mt={6}>
                  <Button
                    leftIcon={<Icon as={MessageSquare} />}
                    colorScheme="teal"
                    size="lg"
                    onClick={onOpenJIMIChat}
                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                    transition="all 0.2s"
                  >
                    Ask JIMI Assistant
                  </Button>
                </Flex>
              </VStack>
            </Box>
          </VStack>
        </MotionBox>
      </Flex>
    </Box>
  );
}
